// src/services/apiConfig.ts
// ============================================
// API Key 热插拔配置系统
//
// 功能：
//   1. 统一管理所有第三方 API 的 Key、BaseURL、状态
//   2. 支持运行时热更新（无需重启）
//   3. 持久化到 localStorage（浏览器本地）
//   4. 提供获取接口：getApiKey / getApiBase / getStatus
//   5. 设置页面可视化配置
//
// 支持的 API：
//   - duomi: 多米 API（图片/视频/音乐通用平台）
//   - deepseek: DeepSeek（脚本生成）
//   - wechat: 微信公众号（自动发布）
//   - suno: Suno 音乐（备用，已被多米替代）
// ============================================

export interface ApiConfig {
  /** API 标识 */
  id: string;
  /** 显示名称 */
  name: string;
  /** API 密钥 */
  apiKey: string;
  /** API 基础地址 */
  apiBase: string;
  /** 是否启用 */
  enabled: boolean;
  /** 描述 */
  description: string;
  /** 配置项（额外参数，如 AppID、AppSecret） */
  extra?: Record<string, string>;
  /** 最后更新时间 */
  updatedAt: string;
}

export interface ApiConfigUpdate {
  apiKey?: string;
  apiBase?: string;
  enabled?: boolean;
  extra?: Record<string, string>;
}

// ============================================
// 默认配置（从环境变量读取，降级到默认值）
// ============================================

const DEFAULT_CONFIGS: Record<string, ApiConfig> = {
  duomi: {
    id: 'duomi',
    name: '多米 API（图片/视频/音乐通用平台）',
    apiKey: '',
    apiBase: '/duomi-api/v1',
    enabled: true,
    description: '多米 API 通用平台，支持图片生成、视频生成、音乐生成。密钥由后端 /duomi-api 代理统一代填，前端不持有（防止 bundle 泄漏）。',
    updatedAt: new Date().toISOString(),
  },
  deepseek: {
    id: 'deepseek',
    name: 'DeepSeek（脚本生成）',
    apiKey: '',
    apiBase: '/deepseek-api/v1',
    enabled: true,
    description: 'DeepSeek Chat API，用于生成公众号文章脚本、视频分镜脚本、小红书图文脚本。密钥由后端 /deepseek-api 代理统一代填，前端不持有。',
    updatedAt: new Date().toISOString(),
  },
  wechat: {
    id: 'wechat',
    name: '微信公众号（自动发布）',
    apiKey: '',
    apiBase: '/wechat-api/cgi-bin',
    enabled: false,
    description: '微信公众号 API，用于自动发布图文素材。需要配置 AppID 和 AppSecret。通过 Vite 代理 /wechat-api 转发请求。',
    extra: {
      appId: '',
      appSecret: '',
    },
    updatedAt: new Date().toISOString(),
  },
  suno: {
    id: 'suno',
    name: 'Suno 音乐（备用）',
    apiKey: '',
    apiBase: '/suno-api',
    enabled: false,
    description: 'Suno 音乐生成 API（备用，已被多米 API 替代）。',
    updatedAt: new Date().toISOString(),
  },
};

// ============================================
// 存储键名
// ============================================

const STORAGE_KEY = 'xeaos_api_configs';

// ============================================
// 配置管理器
// ============================================

class ApiConfigManager {
  private configs: Record<string, ApiConfig> = {};
  private listeners: Set<() => void> = new Set();

  constructor() {
    this.load();
  }

  /** 从 localStorage 加载配置，合并默认值 */
  private load() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // 合并：默认值为基础，存储的值覆盖
        this.configs = { ...DEFAULT_CONFIGS };
        for (const key of Object.keys(parsed)) {
          if (this.configs[key]) {
            this.configs[key] = { ...this.configs[key], ...parsed[key] };
          } else {
            this.configs[key] = parsed[key];
          }
        }
      } else {
        // 首次：初始化默认配置。
        // P12 bundle 密钥治理：DeepSeek 密钥由后端统一托管代填，前端不再从 env/localStorage 持有，
        // 避免 VITE_DEEPSEEK_API_KEY 被 Vite 内联进前端 bundle。
        this.configs = { ...DEFAULT_CONFIGS };
        this.save();
      }

      // 每次加载都从 .env 同步微信配置（修复"已提供 app id 仍显示未配置"问题）
      // 原因：localStorage 中可能存储了空 appId，需要从 .env 补充
      const wechatAppId = this.readEnvKey('VITE_WECHAT_APP_ID', 'WECHAT_APP_ID');
      if (wechatAppId) {
        if (!this.configs.wechat.extra) {
          this.configs.wechat.extra = {};
        }
        // 如果 localStorage 中没有 appId 或为空，则从 .env 读取
        if (!this.configs.wechat.extra.appId) {
          this.configs.wechat.extra.appId = wechatAppId;
        }
        // P12 短板4/5/8：AppSecret 由后端 Vault 托管代填，前端不再把密钥写入 localStorage。
        // 已配置 AppID 即视为启用（密钥不足时后端代理会返回微信官方错误码提示）。
        this.configs.wechat.enabled = true;
      }

      // 每次加载不再从 .env 同步 DeepSeek Key：
      // P12 bundle 密钥治理 —— 前端不持有 DeepSeek 密钥，统一由后端 /deepseek-api 代理代填。

      this.save();
    } catch {
      this.configs = { ...DEFAULT_CONFIGS };
    }
  }

  /** 保存到 localStorage */
  private save() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.configs));
    } catch {
      // 忽略存储失败
    }
  }

  /** 读取环境变量（兼容 Vite 和 Node） */
  private readEnvKey(viteKey: string, nodeKey: string): string {
    try {
      const viteEnv = (import.meta as any).env;
      if (viteEnv && viteEnv[viteKey]) return viteEnv[viteKey];
      if (typeof process !== 'undefined' && process.env?.[nodeKey]) {
        return process.env[nodeKey];
      }
    } catch {
      // ignore
    }
    return '';
  }

  /** 获取所有配置 */
  getAll(): ApiConfig[] {
    return Object.values(this.configs);
  }

  /** 获取单个配置 */
  get(id: string): ApiConfig | undefined {
    return this.configs[id];
  }

  /** 获取 API Key */
  getApiKey(id: string): string {
    return this.configs[id]?.apiKey || '';
  }

  /** 获取 API Base URL */
  getApiBase(id: string): string {
    return this.configs[id]?.apiBase || '';
  }

  /** 检查 API 是否启用 */
  isEnabled(id: string): boolean {
    return this.configs[id]?.enabled ?? false;
  }

  /** 获取额外配置项 */
  getExtra(id: string, key: string): string {
    return this.configs[id]?.extra?.[key] || '';
  }

  /** 更新配置 */
  update(id: string, update: ApiConfigUpdate): void {
    if (!this.configs[id]) return;
    if (update.apiKey !== undefined) this.configs[id].apiKey = update.apiKey;
    if (update.apiBase !== undefined) this.configs[id].apiBase = update.apiBase;
    if (update.enabled !== undefined) this.configs[id].enabled = update.enabled;
    if (update.extra !== undefined) {
      this.configs[id].extra = { ...this.configs[id].extra, ...update.extra };
    }
    this.configs[id].updatedAt = new Date().toISOString();
    this.save();
    this.notifyListeners();
  }

  /** 重置为默认配置 */
  reset(id: string): void {
    if (DEFAULT_CONFIGS[id]) {
      this.configs[id] = { ...DEFAULT_CONFIGS[id], updatedAt: new Date().toISOString() };
      this.save();
      this.notifyListeners();
    }
  }

  /** 重置全部 */
  resetAll(): void {
    this.configs = { ...DEFAULT_CONFIGS };
    this.save();
    this.notifyListeners();
  }

  /** 测试 API 连接 */
  async test(id: string): Promise<{ success: boolean; message: string; latency?: number }> {
    const config = this.configs[id];
    if (!config) return { success: false, message: '配置不存在' };
    if (!config.apiKey) return { success: false, message: 'API Key 未配置' };

    const startTime = Date.now();
    try {
      let testUrl = '';
      let headers: Record<string, string> = { Authorization: `Bearer ${config.apiKey}` };

      switch (id) {
        case 'duomi':
          testUrl = '/duomi-api/v1/models';
          break;
        case 'deepseek':
          testUrl = '/deepseek-api/v1/models';
          break;
        case 'wechat':
          // P12 短板4/5/8：AppSecret 由后端 Vault 代填，前端不再把密钥拼进 URL
          if (!config.extra?.appId && !config.extra?.appSecret) {
            return { success: false, message: 'AppID 或 AppSecret 未配置' };
          }
          testUrl = `${config.apiBase}/token?grant_type=client_credential`;
          if (config.extra?.appId) testUrl += `&appid=${encodeURIComponent(config.extra.appId)}`;
          headers = {};
          break;
        case 'suno':
          testUrl = '/suno-api/api/suno/status';
          break;
        default:
          return { success: false, message: '未知 API 类型' };
      }

      const resp = await fetch(testUrl, { headers });
      const latency = Date.now() - startTime;

      if (resp.ok) {
        return { success: true, message: '连接成功', latency };
      }
      return { success: false, message: `HTTP ${resp.status}`, latency };
    } catch (err: any) {
      return { success: false, message: err?.message || '连接失败' };
    }
  }

  /** 订阅配置变更 */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** 通知所有订阅者 */
  private notifyListeners() {
    this.listeners.forEach((l) => l());
  }
}

// ============================================
// 单例
// ============================================

export const apiConfig = new ApiConfigManager();
export default apiConfig;
